import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Config as MentionConfig } from '@app/sharedModule/components/mention-collaboration/config';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { DatePickerProfileForm } from './components/date-picker-tab/date-picker-tab.utils';

export const USAGE_TOKEN_PREFIX = 'multi_shipment_';
export const USAGE_TOKEN_RANDOM_LENGTH = 12;

export const MULTI_SHIPMENT_DISPLAY_RULES = getExtensionRules();

/** Style choices shared by every section banner. Spread on use so each section keeps its own array. */
export const BANNER_STYLE_OPTIONS: DropdownOption[] = [
  { colorTag: 'var(--surface-dim)', label: 'Info', value: 'info' },
  { colorTag: 'var(--success-default)', label: 'Success', value: 'success' },
  { colorTag: 'var(--warning-default)', label: 'Warning', value: 'warning' },
  { colorTag: 'var(--error-default)', label: 'Critical', value: 'danger' },
];

/** Emphasis choices for the hardcoded "items still need to be assigned" message. */
export const TEXT_TONE_OPTIONS: DropdownOption[] = [
  { colorTag: 'var(--surface-dim)', label: 'None', value: 'none' },
  { colorTag: 'var(--warning-default)', label: 'Warning', value: 'warning' },
  { colorTag: 'var(--error-default)', label: 'Error', value: 'critical' },
];

/** Tokens a merchant can drop into label/title fields; resolved to live values in the checkout UI. */
const LABEL_VARIABLE_DEFINITIONS = {
  quantity: { name: '{quantity}', description: 'Quantity', searchTerm: 'quantity', replaceableName: '{quantity}' },
  name: { name: '{name}', description: 'Recipient name', searchTerm: 'name', replaceableName: '{name}' },
  number: { name: '{number}', description: 'Recipient number', searchTerm: 'number', replaceableName: '{number}' },
};

export type LabelVariable = keyof typeof LABEL_VARIABLE_DEFINITIONS;

/** Builds the mention-autocomplete config that pops the given variables when the merchant types `{`. */
export function createLabelVariablesConfig(variables: LabelVariable[]): MentionConfig {
  return {
    items: variables.map((variable) => LABEL_VARIABLE_DEFINITIONS[variable]),
    triggerChar: '{',
    labelKey: 'description',
    descriptionKey: 'name',
    filterByKey: 'searchTerm',
    removeTriggerCharOnSelect: true,
  };
}

/** Single source of default values for every control in the Multi-shipment form. */
export type MultiShipmentDefaults = Required<
  Omit<MultiShipmentExtension.Template, 'enabled' | 'displayRules' | 'datePickerProfiles'>
>;

export const MULTI_SHIPMENT_DEFAULTS: MultiShipmentDefaults = {
  internalName: 'Multi Shipment',
  internalDescription: '',
  shippingToggle: {
    defaultSelection: 'single',
    singleAddress: { title: 'Ship to one address', description: 'All items go to the same delivery address' },
    multiAddress: {
      title: 'Ship to multiple addresses',
      description: 'Send items to different recipients, each with their own address, date, and gift message',
    },
  },
  contactSectionBanner: {
    text: "We'll send the order confirmation and shipping updates to the email above.",
    style: 'info',
  },
  deliverySectionBanner: {
    text: 'Enter the delivery details for your first recipient below. You can add more recipients after.',
    style: 'info',
  },
  recipientsBlock: {
    addRecipientCard: {
      title: 'Choose their gift',
      helpText: 'Pick items, a delivery date, and an optional gift note for this recipient.',
      buttonLabel: 'Choose items',
    },
    addAnotherRecipientCard: {
      enabled: false,
      title: 'Add another recipient',
      helpText: 'Pick items, a delivery date, and an optional gift note for this recipient.',
      buttonLabel: 'Choose items',
    },
    unassignedItemsWarningTone: 'warning',
  },
  giftNotesBlock: {
    title: 'Gift notes',
    description: 'Optional handwritten cards for the gifts in this shipment.',
    addGiftNoteCard: {
      title: 'Add a gift note',
      helpText: 'Include a handwritten card with the gifts in this shipment.',
      buttonLabel: 'Add note',
    },
    addAnotherGiftNoteCard: {
      enabled: false,
      title: 'Add another gift note',
      helpText: 'Include a handwritten card with the gifts in this shipment.',
      buttonLabel: 'Add note',
    },
  },
  addRecipientModal: {
    addRecipientTitle: 'Recipient {number}',
    editRecipientTitle: 'Edit {name}',
    addGiftNoteTitle: 'Gift note {number}',
    editGiftNoteTitle: 'Edit gift note for {name}',
    items: {
      title: 'Which items for this recipient?',
      description: 'Use + and – to assign quantities. Items fully assigned to other recipients are greyed out.',
    },
    address: {
      title: 'Where should this gift go?',
      description: "Enter the recipient's address. We'll calculate shipping and tax for this destination.",
    },
    shipping: {
      title: 'Select shipping method',
      description: '',
      noRates: {
        title: 'Shipping not available',
        body: 'Your order cannot be shipped to the selected address and delivery date.',
      },
    },
    giftNote: {
      title: 'Add a gift note',
      description: "We'll handwrite this on a gift card tucked inside the package.",
      messageLimit: 240,
    },
  },
  lineItemAssignmentStatus: {
    assigned: {
      label: '{quantity}× To {name} (#{number})',
      backgroundColor: '#000000',
      textColor: '#EDEDED',
      cornerRadius: 'small',
    },
    unassigned: {
      label: '{quantity}× Unassigned',
      backgroundColor: '#FDD8D8',
      textColor: '#420000',
      cornerRadius: 'small',
    },
  },
};

/*
 * Extensions saved before a field existed arrive with partial blocks — a @recipientsBlock without
 * @addRecipientCard, a @shipping step without @noRates. Filling those gaps from the complete defaults tree
 * keeps the builders below safe and makes a plain Save rewrite the record in the current schema.
 */
export function withMultiShipmentDefaults(
  loadedValues?: Partial<MultiShipmentExtension.Template>,
): MultiShipmentDefaults {
  return fillMissingFromDefaults(
    MULTI_SHIPMENT_DEFAULTS as unknown as Record<string, unknown>,
    (loadedValues ?? {}) as Record<string, unknown>,
  ) as unknown as MultiShipmentDefaults;
}

function fillMissingFromDefaults(
  defaults: Record<string, unknown>,
  loaded: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...defaults };

  for (const [key, loadedValue] of Object.entries(loaded)) {
    if (loadedValue === undefined || loadedValue === null) {
      continue;
    }
    const defaultValue = merged[key];
    merged[key] =
      isPlainObject(defaultValue) && isPlainObject(loadedValue)
        ? fillMissingFromDefaults(defaultValue, loadedValue)
        : loadedValue;
  }

  return merged;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface ShippingToggleOptionForm {
  title: FormControl<string>;
  description: FormControl<string>;
}

export interface ShippingToggleForm {
  defaultSelection: FormControl<MultiShipmentExtension.ShippingMode>;
  singleAddress: FormGroup<ShippingToggleOptionForm>;
  multiAddress: FormGroup<ShippingToggleOptionForm>;
}

export interface SectionBannerForm {
  text: FormControl<string>;
  style: FormControl<MultiShipmentExtension.BannerStyle>;
}

export interface ActionCardForm {
  title: FormControl<string>;
  helpText: FormControl<string>;
  buttonLabel: FormControl<string>;
}

export interface AddAnotherCardForm extends ActionCardForm {
  enabled: FormControl<boolean>;
}

export interface RecipientsBlockForm {
  addRecipientCard: FormGroup<ActionCardForm>;
  addAnotherRecipientCard: FormGroup<AddAnotherCardForm>;
  unassignedItemsWarningTone: FormControl<MultiShipmentExtension.TextTone>;
}

export interface GiftNotesBlockForm {
  title: FormControl<string>;
  description: FormControl<string>;
  addGiftNoteCard: FormGroup<ActionCardForm>;
  addAnotherGiftNoteCard: FormGroup<AddAnotherCardForm>;
}

export interface AddRecipientStepForm {
  title: FormControl<string>;
  description: FormControl<string>;
}

export interface NoRatesNoticeForm {
  title: FormControl<string>;
  body: FormControl<string>;
}

export interface ShippingStepForm extends AddRecipientStepForm {
  noRates: FormGroup<NoRatesNoticeForm>;
}

export interface GiftNoteStepForm extends AddRecipientStepForm {
  messageLimit: FormControl<number>;
}

export interface AddRecipientModalForm {
  addRecipientTitle: FormControl<string>;
  editRecipientTitle: FormControl<string>;
  addGiftNoteTitle: FormControl<string>;
  editGiftNoteTitle: FormControl<string>;
  items: FormGroup<AddRecipientStepForm>;
  address: FormGroup<AddRecipientStepForm>;
  shipping: FormGroup<ShippingStepForm>;
  giftNote: FormGroup<GiftNoteStepForm>;
}

export interface AssignmentStatusPillForm {
  label: FormControl<string>;
  backgroundColor: FormControl<string>;
  textColor: FormControl<string>;
  cornerRadius: FormControl<MultiShipmentExtension.PillCornerRadius>;
}

export interface LineItemAssignmentStatusForm {
  assigned: FormGroup<AssignmentStatusPillForm>;
  unassigned: FormGroup<AssignmentStatusPillForm>;
}

export interface MultiShipmentFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  internalDescription: FormControl<string>;
  shippingToggle: FormGroup<ShippingToggleForm>;
  contactSectionBanner: FormGroup<SectionBannerForm>;
  deliverySectionBanner: FormGroup<SectionBannerForm>;
  recipientsBlock: FormGroup<RecipientsBlockForm>;
  giftNotesBlock: FormGroup<GiftNotesBlockForm>;
  addRecipientModal: FormGroup<AddRecipientModalForm>;
  lineItemAssignmentStatus: FormGroup<LineItemAssignmentStatusForm>;
  displayRules: FormArray<FormGroup>;
  datePickerProfiles: FormArray<FormGroup<DatePickerProfileForm>>;
}

export function createSectionBannerForm(value: MultiShipmentExtension.SectionBanner): FormGroup<SectionBannerForm> {
  return new FormGroup<SectionBannerForm>({
    text: new FormControl(value.text, { nonNullable: true, validators: [Validators.required] }),
    style: new FormControl(value.style, { nonNullable: true }),
  });
}

export function createShippingToggleForm(value: MultiShipmentExtension.ShippingToggle): FormGroup<ShippingToggleForm> {
  return new FormGroup<ShippingToggleForm>({
    defaultSelection: new FormControl(value.defaultSelection, { nonNullable: true }),
    singleAddress: createShippingOptionForm(value.singleAddress),
    multiAddress: createShippingOptionForm(value.multiAddress),
  });
}

function createShippingOptionForm(
  option: MultiShipmentExtension.ShippingToggleOption,
): FormGroup<ShippingToggleOptionForm> {
  return new FormGroup<ShippingToggleOptionForm>({
    title: new FormControl(option.title, { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl(option.description, { nonNullable: true, validators: [Validators.required] }),
  });
}

export function createRecipientsBlockForm(
  value: MultiShipmentExtension.RecipientsBlock,
): FormGroup<RecipientsBlockForm> {
  return new FormGroup<RecipientsBlockForm>({
    addRecipientCard: createActionCardForm(value.addRecipientCard),
    addAnotherRecipientCard: createAddAnotherCardForm(
      value.addAnotherRecipientCard ?? MULTI_SHIPMENT_DEFAULTS.recipientsBlock.addAnotherRecipientCard,
    ),
    unassignedItemsWarningTone: new FormControl(value.unassignedItemsWarningTone, { nonNullable: true }),
  });
}

export function createGiftNotesBlockForm(value: MultiShipmentExtension.GiftNotesBlock): FormGroup<GiftNotesBlockForm> {
  return new FormGroup<GiftNotesBlockForm>({
    title: new FormControl(value.title, { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl(value.description, { nonNullable: true }),
    addGiftNoteCard: createActionCardForm(value.addGiftNoteCard),
    addAnotherGiftNoteCard: createAddAnotherCardForm(
      value.addAnotherGiftNoteCard ?? MULTI_SHIPMENT_DEFAULTS.giftNotesBlock.addAnotherGiftNoteCard,
    ),
  });
}

function createActionCardForm(card: MultiShipmentExtension.ActionCard): FormGroup<ActionCardForm> {
  return new FormGroup<ActionCardForm>({
    title: new FormControl(card.title, { nonNullable: true, validators: [Validators.required] }),
    helpText: new FormControl(card.helpText, { nonNullable: true, validators: [Validators.required] }),
    buttonLabel: new FormControl(card.buttonLabel, { nonNullable: true, validators: [Validators.required] }),
  });
}

function createAddAnotherCardForm(card: MultiShipmentExtension.AddAnotherCard): FormGroup<AddAnotherCardForm> {
  return new FormGroup<AddAnotherCardForm>({
    enabled: new FormControl(card.enabled, { nonNullable: true }),
    ...createActionCardForm(card).controls,
  });
}

export function createAddRecipientModalForm(
  value: MultiShipmentExtension.AddRecipientModal,
): FormGroup<AddRecipientModalForm> {
  return new FormGroup<AddRecipientModalForm>({
    addRecipientTitle: new FormControl(value.addRecipientTitle, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    editRecipientTitle: new FormControl(value.editRecipientTitle, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    addGiftNoteTitle: new FormControl(value.addGiftNoteTitle, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    editGiftNoteTitle: new FormControl(value.editGiftNoteTitle, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    items: createAddRecipientStepForm(value.items),
    address: createAddRecipientStepForm(value.address),
    shipping: new FormGroup<ShippingStepForm>({
      ...createAddRecipientStepForm(value.shipping).controls,
      noRates: new FormGroup<NoRatesNoticeForm>({
        title: new FormControl(value.shipping.noRates.title, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        body: new FormControl(value.shipping.noRates.body, { nonNullable: true }),
      }),
    }),
    giftNote: new FormGroup<GiftNoteStepForm>({
      ...createAddRecipientStepForm(value.giftNote).controls,
      messageLimit: new FormControl(value.giftNote.messageLimit, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1)],
      }),
    }),
  });
}

function createAddRecipientStepForm(step: MultiShipmentExtension.AddRecipientStep): FormGroup<AddRecipientStepForm> {
  return new FormGroup<AddRecipientStepForm>({
    title: new FormControl(step.title, { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl(step.description, { nonNullable: true }),
  });
}

export function createLineItemAssignmentStatusForm(
  value: MultiShipmentExtension.LineItemAssignmentStatus,
): FormGroup<LineItemAssignmentStatusForm> {
  return new FormGroup<LineItemAssignmentStatusForm>({
    assigned: createAssignmentStatusPillForm(value.assigned),
    unassigned: createAssignmentStatusPillForm(value.unassigned),
  });
}

function createAssignmentStatusPillForm(
  pill: MultiShipmentExtension.AssignmentStatusPill,
): FormGroup<AssignmentStatusPillForm> {
  return new FormGroup<AssignmentStatusPillForm>({
    label: new FormControl(pill.label, { nonNullable: true, validators: [Validators.required] }),
    backgroundColor: new FormControl(pill.backgroundColor, { nonNullable: true }),
    textColor: new FormControl(pill.textColor, { nonNullable: true }),
    cornerRadius: new FormControl(pill.cornerRadius, { nonNullable: true }),
  });
}
